import { useEffect, useState } from 'react';
import { AlertCircle, Clock, UserMinus, UserPlus } from 'lucide-react';

import { attendanceApi } from '../../services/api';
import type { SessionRegistrationContext, SessionSlot } from '../../types';
import CheckInDialog from '../checkIn/CheckInDialog';
import { formatCenterTime } from '../../utils/geolocation';
import { Button, Modal, TimeInput, useToast } from '../ui';

interface SessionPerson {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
}

export interface StaffSession {
    id: string;
    teacherId?: string | null;
    assistantId?: string | null;
    teacher?: SessionPerson | null;
    assistant?: SessionPerson | null;
    teacherStartTime?: string | null;
    teacherEndTime?: string | null;
    assistantStartTime?: string | null;
    assistantEndTime?: string | null;
}

const slotLabels: Record<SessionSlot, { title: string; register: string }> = {
    teacher: { title: 'Giáo viên', register: 'Đăng ký dạy' },
    assistant: { title: 'Trợ giảng', register: 'Đăng ký trợ giảng' },
};

function personName(person?: SessionPerson | null) {
    return `${person?.firstName ?? ''} ${person?.lastName ?? ''}`.trim() || 'Người khác';
}

function occupantOf(session: StaffSession | null, slot: SessionSlot) {
    if (!session) return { id: null, person: null };
    return slot === 'teacher'
        ? { id: session.teacherId ?? null, person: session.teacher ?? null }
        : { id: session.assistantId ?? null, person: session.assistant ?? null };
}

interface OverlapItem {
    attendanceId: string;
    slot: SessionSlot;
    className: string;
    campusName: string | null;
    startTime: string | null;
    endTime: string | null;
}

export interface SessionSlotCardProps {
    slot: SessionSlot;
    session: StaffSession | null;
    currentUserId: string;
    registration: SessionRegistrationContext | null;
    classInfo: { id: string; name: string; campusId: string };
    onChanged: () => Promise<void> | void;
}

// Registration control for teachers/assistants; admins and managers keep the assignment picker.
export function SessionSlotCard({ slot, session, currentUserId, registration, classInfo, onChanged }: SessionSlotCardProps) {
    const { showToast } = useToast();
    const [busy, setBusy] = useState(false);
    const [checkInOpen, setCheckInOpen] = useState(false);
    const [showOccupiedHelp, setShowOccupiedHelp] = useState(false);
    // Buổi trùng giờ đang chờ xác nhận, kèm lựa chọn đăng ký trước đó (force/reason).
    const [overlap, setOverlap] = useState<{ overlaps: OverlapItem[]; force: boolean; reason: string | null } | null>(null);
    const [nowTime, setNowTime] = useState(() => formatCenterTime(new Date()));
    const occupant = occupantOf(session, slot);
    const labels = slotLabels[slot];
    const opensAt = registration?.isOpen ? registration.opensAt ?? null : null;
    const notOpenYet = Boolean(opensAt && nowTime < opensAt);

    // Unlock the button by itself once the opening time arrives.
    useEffect(() => {
        if (!notOpenYet) return undefined;
        const timer = window.setInterval(() => setNowTime(formatCenterTime(new Date())), 15000);
        return () => window.clearInterval(timer);
    }, [notOpenYet]);
    const targetLabel = `${labels.title.toLowerCase()} lớp ${classInfo.name}`;

    const register = async (force = false, reason: string | null = null, confirmOverlap = false) => {
        setBusy(true);
        try {
            await attendanceApi.register({ classId: classInfo.id, slot, force, reason, confirmOverlap });
            setCheckInOpen(false);
            setOverlap(null);
            showToast(force || confirmOverlap ? 'Đã đăng ký. Quản lý sẽ nhận được thông báo.' : 'Đăng ký thành công.', 'success');
            await onChanged();
        } catch (error: any) {
            if (error?.data?.error === 'SCHEDULE_OVERLAP') {
                setCheckInOpen(false);
                setOverlap({ overlaps: error.data.overlaps ?? [], force, reason });
            } else if (error?.data?.error === 'CHECK_IN_REQUIRED') {
                setCheckInOpen(true);
            } else {
                showToast(error?.message || 'Đăng ký thất bại.', 'error');
                setCheckInOpen(false);
                await onChanged();
            }
        } finally {
            setBusy(false);
        }
    };

    const handleRegisterClick = () => {
        const validUntil = registration?.checkInValidUntil ? new Date(registration.checkInValidUntil).getTime() : 0;
        if (validUntil > Date.now()) {
            register();
        } else {
            setCheckInOpen(true);
        }
    };

    const cancel = async () => {
        if (!session) return;
        setBusy(true);
        try {
            await attendanceApi.cancelRegistration(session.id, slot);
            showToast('Đã huỷ đăng ký.', 'success');
            await onChanged();
        } catch (error: any) {
            showToast(error?.message || 'Huỷ đăng ký thất bại.', 'error');
        } finally {
            setBusy(false);
        }
    };

    let content: React.ReactNode;
    if (occupant.id === currentUserId) {
        content = (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-success-100 bg-success-50 px-3 py-2">
                <span className="text-sm font-medium text-success-800">Bạn ({personName(occupant.person)})</span>
                <Button variant="ghost" size="sm" loading={busy} icon={<UserMinus className="h-4 w-4" />} onClick={cancel}>
                    Huỷ đăng ký
                </Button>
            </div>
        );
    } else if (occupant.id) {
        const name = personName(occupant.person);
        content = (
            <div className="space-y-1">
                <div className="flex items-center justify-between gap-2 rounded-lg border border-ink-200 bg-ink-50 px-3 py-2">
                    <span className="text-sm font-medium text-ink-800">{name}</span>
                    <button
                        type="button"
                        className="rounded-full p-1 text-warning-600 hover:bg-warning-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-warning-100"
                        aria-label={`Muốn nhận vị trí này? Liên hệ quản lý hoặc ${name} để huỷ đăng ký`}
                        title={`Liên hệ quản lý hoặc ${name} để huỷ đăng ký`}
                        aria-expanded={showOccupiedHelp}
                        onClick={() => setShowOccupiedHelp(value => !value)}
                    >
                        <AlertCircle className="h-4 w-4" />
                    </button>
                </div>
                {showOccupiedHelp ? (
                    <p className="text-xs text-warning-700">
                        Vị trí này đã có người đăng ký. Liên hệ quản lý hoặc {name} để huỷ đăng ký nếu bạn cần nhận vị trí này.
                    </p>
                ) : null}
            </div>
        );
    } else if (registration?.isOpen && notOpenYet) {
        content = (
            <Button variant="secondary" className="w-full" disabled icon={<Clock className="h-4 w-4" />}>
                Mở đăng ký lúc {opensAt}
            </Button>
        );
    } else if (registration?.isOpen) {
        content = (
            <Button
                variant="secondary"
                className="w-full"
                loading={busy}
                icon={<UserPlus className="h-4 w-4" />}
                onClick={handleRegisterClick}
            >
                {labels.register}
            </Button>
        );
    } else {
        content = (
            <p className="rounded-lg border border-dashed border-ink-200 px-3 py-2 text-sm text-ink-500">
                Chưa có người đăng ký
            </p>
        );
    }

    return (
        <div>
            <p className="mb-1 block text-sm font-medium text-ink-700">{labels.title}</p>
            {content}
            <CheckInDialog
                open={checkInOpen}
                mode="registration"
                targetCampusId={classInfo.campusId}
                targetLabel={targetLabel}
                onClose={() => setCheckInOpen(false)}
                onCheckedIn={() => register()}
                onForceRegister={reason => register(true, reason)}
            />
            <Modal
                open={Boolean(overlap)}
                title="Trùng giờ với buổi bạn đã nhận"
                onClose={() => setOverlap(null)}
                footer={(
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button variant="secondary" onClick={() => setOverlap(null)}>Không đăng ký</Button>
                        <Button loading={busy} onClick={() => overlap && register(overlap.force, overlap.reason, true)}>
                            Vẫn đăng ký
                        </Button>
                    </div>
                )}
            >
                <div className="space-y-3 text-sm text-ink-700">
                    <p>Hôm nay bạn đã nhận các buổi sau, trùng giờ với {targetLabel}:</p>
                    <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
                        {overlap?.overlaps.map(item => (
                            <li key={`${item.attendanceId}-${item.slot}`} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                                <span>
                                    <span className="font-medium text-ink-900">{item.className}</span>
                                    {item.campusName ? <span className="text-ink-500"> · {item.campusName}</span> : null}
                                    <span className="text-ink-500"> · {slotLabels[item.slot]?.title.toLowerCase()}</span>
                                </span>
                                <span className="tabular-nums text-ink-900">{item.startTime}–{item.endTime}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="text-warning-800">Nếu vẫn đăng ký, quản lý sẽ nhận được thông báo để kiểm tra.</p>
                </div>
            </Modal>
        </div>
    );
}

export interface SessionStaffTimesProps {
    session: StaffSession | null;
    currentUserId: string;
    isManager: boolean;
    defaultTimes: { startTime: string | null; endTime: string | null };
    onChanged: () => Promise<void> | void;
}

function SlotTimesRow({ slot, session, currentUserId, isManager, defaultTimes, onChanged }: SessionStaffTimesProps & { slot: SessionSlot }) {
    const { showToast } = useToast();
    const occupant = occupantOf(session, slot);
    const savedStart = (slot === 'teacher' ? session?.teacherStartTime : session?.assistantStartTime) ?? defaultTimes.startTime ?? '';
    const savedEnd = (slot === 'teacher' ? session?.teacherEndTime : session?.assistantEndTime) ?? defaultTimes.endTime ?? '';
    const [startTime, setStartTime] = useState(savedStart);
    const [endTime, setEndTime] = useState(savedEnd);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setStartTime(savedStart);
        setEndTime(savedEnd);
    }, [savedStart, savedEnd]);

    if (!session || !occupant.id) return null;

    const canEdit = isManager || occupant.id === currentUserId;
    const dirty = startTime !== savedStart || endTime !== savedEnd;
    const inputId = `${session.id}-${slot}`;

    const save = async () => {
        if (!startTime || !endTime) {
            showToast('Vui lòng nhập đủ giờ vào và giờ ra.', 'error');
            return;
        }
        if (startTime >= endTime) {
            showToast('Giờ ra phải sau giờ vào.', 'error');
            return;
        }
        setSaving(true);
        try {
            await attendanceApi.updateStaffTimes(session.id, { slot, startTime, endTime });
            showToast(isManager ? 'Đã lưu giờ.' : 'Đã lưu giờ. Quản lý sẽ nhận thông báo về thay đổi này.', 'success');
            await onChanged();
        } catch (error: any) {
            showToast(error?.message || 'Lưu giờ thất bại.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex flex-wrap items-end gap-3">
            <p className="w-full text-sm font-medium text-ink-700 sm:w-40">
                {slotLabels[slot].title}: <span className="font-normal text-ink-600">{personName(occupant.person)}</span>
            </p>
            <label className="text-xs text-ink-500" htmlFor={`${inputId}-start`}>
                Giờ vào
                <TimeInput
                    id={`${inputId}-start`}
                    value={startTime}
                    disabled={!canEdit}
                    onChange={setStartTime}
                    className="mt-1 block w-20 rounded-lg border border-ink-300 px-2 py-1.5 text-sm text-ink-900 focus:border-academy-500 focus:ring-2 focus:ring-academy-200 disabled:bg-ink-100"
                />
            </label>
            <label className="text-xs text-ink-500" htmlFor={`${inputId}-end`}>
                Giờ ra
                <TimeInput
                    id={`${inputId}-end`}
                    value={endTime}
                    disabled={!canEdit}
                    onChange={setEndTime}
                    className="mt-1 block w-20 rounded-lg border border-ink-300 px-2 py-1.5 text-sm text-ink-900 focus:border-academy-500 focus:ring-2 focus:ring-academy-200 disabled:bg-ink-100"
                />
            </label>
            {canEdit && dirty ? (
                <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => { setStartTime(savedStart); setEndTime(savedEnd); }}>
                        Hoàn tác
                    </Button>
                    <Button size="sm" loading={saving} onClick={save}>Lưu giờ</Button>
                </div>
            ) : null}
        </div>
    );
}

// Actual start/end per person; defaults to the class hours for that day.
export function SessionStaffTimes(props: SessionStaffTimesProps) {
    const hasOccupant = Boolean(props.session?.teacherId || props.session?.assistantId);
    if (!hasOccupant) return null;

    return (
        <div className="space-y-3 rounded-lg border border-ink-200 bg-ink-50 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                <Clock className="h-4 w-4" />
                Giờ vào / giờ ra
            </div>
            <SlotTimesRow slot="teacher" {...props} />
            <SlotTimesRow slot="assistant" {...props} />
            {!props.isManager ? (
                <p className="text-xs text-ink-500">
                    Mặc định theo giờ của lớp. Nếu bạn sửa giờ của mình, quản lý sẽ nhận được thông báo.
                </p>
            ) : null}
        </div>
    );
}
